import { Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createExtraMembership } from 'src/shared/services/extraMembershipService';
import { createMemberInvoice } from 'src/shared/services/memberInvoiceService';
import { useExtraMembershipLabel } from '../hooks/useExtraMembershipLabel';
import { MemberCreateModeSelector } from './memberCreateModeSelector';

type MemberExtraMembershipCreateModalProps = {
  modalOpen: boolean;
  memberId: number;
  // På Min sida faktureras vänmedlemskapet direkt. I adminvyn fakturerar handläggaren själv.
  createInvoice?: boolean;
  onClose: () => void;
  refetchMemberCallback: () => void;
  // Anropas med den nya medlemspostens id, så att adminvyn kan öppna redigeravyn direkt.
  onCreated?: (memberId: number) => void;
};

/**
 * Skapar ett vänmedlemskap, alltså ett extra medlemskap i en annan förening. Vänposten blir en egen
 * medlemspost med eget medlemsnummer, med kontaktuppgifterna hämtade från det ordinarie
 * medlemskapet.
 *
 * Flödet följer Skapa ny medlem: först frågan om medlemskapet är betalt, sedan föreningen.
 */
export const MemberExtraMembershipCreateModal = (props: MemberExtraMembershipCreateModalProps) => {
  const { modalOpen, memberId, createInvoice = false, onClose, refetchMemberCallback, onCreated = undefined } = props;
  const { t } = useTranslation(['member']);
  const label = useExtraMembershipLabel();

  const [paid, setPaid] = useState<boolean | undefined>(undefined);
  const [localAssId, setLocalAssId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const save = async () => {
    if (paid === undefined || !localAssId) return;

    setIsSaving(true);

    try {
      const created = await createExtraMembership({ memberId, localAssId, paid });

      if (createInvoice && created.data?.memberId) {
        // Fakturan skapas på den nya posten, inte på det ordinarie medlemskapet. Returneras en
        // betallänk öppnas den, precis som i hushållsflödet.
        const invoice = await createMemberInvoice({
          mainMemberId: created.data.memberId,
          onlyInterested: true,
          formName: 'member',
        });

        if (invoice.data && invoice.data.length > 0) window.open(invoice.data);
      }

      toast.success(<>{t('member:extraMembership.createSuccess', { label })}</>);
      refetchMemberCallback();
      onClose();

      if (onCreated && created.data?.memberId) onCreated(created.data.memberId);
    } catch (e) {
      // Serverns kontroller, till exempel att det ordinarie medlemskapet inte är betalt, kastar
      // UserException. Den blir 400 med meddelandet, och interceptorn visar det som toast.
      // Dialogen lämnas öppen så att föreningen kan ändras.
      ConsoleHelper.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      maxWidth='sm'
      dialogTitle={t('member:extraMembership.createTitle', { label: label.toLowerCase() })}
      dialogContent={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='body2' color='text.secondary'>
              {t('member:extraMembership.createInfo', { label: label.toLowerCase() })}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <MemberCreateModeSelector
              payingMember={paid}
              question={t('member:extraMembership.paidQuestion', { label: label.toLowerCase() }) as string}
              payingMemberCallback={(value) => setPaid(value)}
              showAssociation={paid !== undefined}
              associationCallback={(value) => setLocalAssId(value ?? 0)}
            />
          </Grid>
        </Grid>
      }
      saveLabel='button.create'
      onSave={save}
      onClose={() => {
        if (!isSaving) onClose();
      }}
    />
  );
};
